import PersianSupportNumberInput from "@/components/persianNumberInput";
import { useUserFormContext } from "@/pages/dashboard/teams/teamMembers/add/Context";
import { Badge, Group, Paper, Radio, Stack, Switch, Text, Title } from "@mantine/core";
import { IconPercentage } from "@tabler/icons-react";
import { useState } from "react";

const accessLevels = [
  {
    value: "Barber",
    label: "کاربر عادی",
    hint: "مناسب آرایشگر و پرسنل",
    description: "تقویم، مشتریان، ثبت فاکتور، فاکتورها، نوبت‌های آنلاین و بازاریابی. تنظیمات سالن، تیم و تسویه را نمی‌بیند.",
  },
  {
    value: "Admin",
    label: "دسترسی کامل",
    hint: "مناسب مدیر یا شریک",
    description: "همه بخش‌ها را می‌بیند و تغییر می‌دهد؛ از جمله خدمات، تیم، شیفت‌ها و تسویه.",
  },
];

const SAMPLE_PRICE = 1_000_000;
const rial = (value: number) => `${Math.round(value).toLocaleString("fa-IR")} ریال`;

const MemberType = ({ commissionLocked = false }: { commissionLocked?: boolean }) => {
  const form = useUserFormContext();
  // The add form is uncontrolled and the edit form controlled; watching keeps this step in sync with both.
  const initial = form.getValues();
  const [level, setLevel] = useState<string | undefined>(initial.merchantUserTypeForm);
  const [commission, setCommission] = useState(initial.markupPercentageFrom);
  const [showOnWebsite, setShowOnWebsite] = useState(initial.ShowOnWebsite ?? true);
  form.watch("merchantUserTypeForm", ({ value }) => setLevel(value));
  form.watch("markupPercentageFrom", ({ value }) => setCommission(value));
  form.watch("ShowOnWebsite", ({ value }) => setShowOnWebsite(value ?? true));

  const percent = Number(commission);
  const hasPercent = commission !== undefined && commission !== null && commission !== "" && percent >= 0 && percent <= 100;

  return (
    <Stack gap="xl" mb="20px">
      <Radio.Group
        value={level ?? null}
        onChange={(value) => form.setFieldValue("merchantUserTypeForm", value)}
        label={<Text span fw={700} size="md">سطح دسترسی</Text>}
        description="این فرد به چه بخش‌هایی دسترسی داشته باشد؟"
        withAsterisk
      >
        <Stack gap="xs" mt="sm">
          {accessLevels.map((item) => (
            <Radio.Card key={item.value} value={item.value} radius="md" p="md">
              <Group wrap="nowrap" align="flex-start">
                <Radio.Indicator mt={3} />
                <Stack gap={2}>
                  <Group gap={6}>
                    <Text fw={700}>{item.label}</Text>
                    <Badge size="xs" variant="light">
                      {item.hint}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed" lh={1.8}>
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </Radio.Card>
          ))}
        </Stack>
      </Radio.Group>

      <Stack gap="xs">
        <Stack gap="0">
          <Title order={5}>کمیسیون سالن</Title>
          <Text size="sm" pt="xs">
            چند درصد از مبلغ هر خدمتِ این فرد سهم سالن باشد؟ (۰ تا ۱۰۰)
          </Text>
        </Stack>
        <PersianSupportNumberInput
          rightSection={<IconPercentage size="15" />}
          size="lg"
          withAsterisk
          withThousandsSeparator
          styles={{
            input: {
              direction: "ltr",
              textAlign: "left",
              paddingLeft: "2.5rem",
            },
            error: {
              fontSize: "0.8rem",
            },
          }}
          onChangeHandler={(e) =>
            form.setValues({
              markupPercentageFrom: e,
            })
          }
          {...form.getInputProps("markupPercentageFrom")}
          disabled={commissionLocked}
          description={commissionLocked ? "آرایشگر اجاره‌ای است؛ کمیسیون سالن روی او اعمال نمی‌شود." : undefined}
        />
        {/* Mirrors the backend: the member is paid gross × (1 − commission%). */}
        <Paper bg="gray.0" radius="md" p="sm">
          <Text size="sm" lh={1.9}>
            {hasPercent ? (
              <>
                مثال: با {percent.toLocaleString("fa-IR")}٪، از خدمت {rial(SAMPLE_PRICE)}ی،{" "}
                <b>{rial((SAMPLE_PRICE * percent) / 100)}</b> سهم سالن و{" "}
                <b>{rial(SAMPLE_PRICE - (SAMPLE_PRICE * percent) / 100)}</b> سهم این فرد است.
              </>
            ) : (
              <>سهم این فرد در «تسویه تیم» حساب می‌شود. ۰ یعنی کل مبلغ هر خدمت برای خود اوست.</>
            )}
          </Text>
        </Paper>
      </Stack>

      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2}>
          <Text fw={700}>نمایش در سایت سالن</Text>
          <Text size="sm" c="dimmed" lh={1.8}>
            اگر روشن باشد، مشتری او را در سایت می‌بیند و می‌تواند با او نوبت بگیرد.
          </Text>
        </Stack>
        <Switch
          size="md"
          mt={4}
          aria-label="نمایش در سایت سالن"
          checked={showOnWebsite}
          onChange={(event) => form.setFieldValue("ShowOnWebsite", event.currentTarget.checked)}
        />
      </Group>
    </Stack>
  );
};

export default MemberType;
